import React, { useEffect, useState } from "react";
import Back from "./common/Back";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../config/config";
import StripeCheckout from "./StripeCheckout";
import SEO from "./SEO";
import { brands } from "../config/config";


// --------------------
// USER / GUEST ID HELPERS
// --------------------
const getUserOrGuestId = () => {
  const user = localStorage.getItem("user");

  if (user) {
    try {
      const parsed = JSON.parse(user);
      if (parsed?.id) return { user_id: parsed.id, guest_id: null };
    } catch { }
  }

  let guest_id = localStorage.getItem("guest_id");
  if (!guest_id) {
    guest_id = "guest_" + Math.random().toString(36).substring(2, 12);
    localStorage.setItem("guest_id", guest_id);
  }

  return { user_id: null, guest_id };
};

// Helper function to calculate delivery date
const calculateDeliveryDate = (daysToAdd) => {
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
};

const Checkout = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // --------------------
  // BILLING FORM STATES
  // --------------------
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [errors, setErrors] = useState({});
  const [zip, setZip] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  // --------------------
  // SHIPPING ADDRESS STATES
  // --------------------
  const [isShippingSame, setIsShippingSame] = useState(true);
  const [shippingFirstName, setShippingFirstName] = useState("");
  const [shippingLastName, setShippingLastName] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingCountry, setShippingCountry] = useState("");
  const [shippingZip, setShippingZip] = useState("");
  const [shippingErrors, setShippingErrors] = useState({});

  // --------------------
  // DELIVERY & SHIPPING STATES
  // --------------------
  const [isRushDelivery, setIsRushDelivery] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(calculateDeliveryDate(14)); // Default 14 days
  const [shippingCharge, setShippingCharge] = useState(20); // Default flat rate $20

  const navigate = useNavigate();
  const subtotal = cartItems.reduce((sum, item) => sum + Number(item.price), 0);

  // Load countries
  useEffect(() => {
    fetch("https://backend.inkox.com/api/countries")
      .then((res) => res.json())
      .then((data) => setCountries(data))
      .catch((err) => console.error("Country fetch error:", err));
  }, []);

  // Update delivery date and shipping charge when rush delivery toggles
  useEffect(() => {
    if (isRushDelivery) {
      setDeliveryDate(calculateDeliveryDate(7)); // 7 days for rush
      setShippingCharge(20 + (subtotal * 0.20)); // 20% of subtotal added
    } else {
      setDeliveryDate(calculateDeliveryDate(14)); // 14 days for normal
      setShippingCharge(20); // Flat $20
    }
  }, [isRushDelivery, subtotal]);

  // When billing address changes and shipping is same as billing, update shipping fields
  useEffect(() => {
    if (isShippingSame) {
      setShippingFirstName(firstName);
      setShippingLastName(lastName);
      setShippingAddress(address);
      setShippingCity(city);
      setShippingCountry(country);
      setShippingZip(zip);
    }
  }, [isShippingSame, firstName, lastName, address, city, country, zip]);

  // --------------------
  // FETCH CART
  // --------------------
  useEffect(() => {
    const { user_id, guest_id } = getUserOrGuestId();

    const url = user_id
      ? `${apiUrl}/api/cart/user/${user_id}`
      : `${apiUrl}/api/cart?guest_id=${guest_id}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCartItems(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Checkout fetch error:", err);
        setLoading(false);
      });
  }, []);

  // --------------------
  // PRICE CALCULATION
  // --------------------


  const total = subtotal + shippingCharge;

  // --------------------
  // VALIDATION FUNCTIONS
  // --------------------
  const validateBillingForm = () => {
    const newErrors = {};

    if (!firstName) newErrors.firstName = true;
    if (!lastName) newErrors.lastName = true;
    if (!address) newErrors.address = true;
    if (!city) newErrors.city = true;
    if (!country) newErrors.country = true;
    if (!zip) newErrors.zip = true;
    if (!mobile) newErrors.mobile = true;
    if (!email) newErrors.email = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateShippingForm = () => {
    if (isShippingSame) return true;

    const newErrors = {};

    if (!shippingFirstName) newErrors.shippingFirstName = true;
    if (!shippingLastName) newErrors.shippingLastName = true;
    if (!shippingAddress) newErrors.shippingAddress = true;
    if (!shippingCity) newErrors.shippingCity = true;
    if (!shippingCountry) newErrors.shippingCountry = true;
    if (!shippingZip) newErrors.shippingZip = true;

    setShippingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAllForms = () => {
    const isBillingValid = validateBillingForm();
    const isShippingValid = validateShippingForm();
    return isBillingValid && isShippingValid;
  };

  // --------------------
  // PLACE ORDER FUNCTION
  // --------------------
  const placeOrder = async (payment_id, paymentStatus = "paid") => {
    const { user_id, guest_id } = getUserOrGuestId();

    // Prepare shipping address object
    const shippingAddressObj = {
      first_name: shippingFirstName,
      last_name: shippingLastName,
      address: shippingAddress,
      city: shippingCity,
      country: shippingCountry,
      zip: shippingZip,
      is_same_as_billing: isShippingSame
    };

    const payload = {
      user_id,
      guest_id,
      first_name: firstName,
      last_name: lastName,
      address,
      city,
      country,
      zip,
      mobile,
      email,
      notes,
      subtotal,
      shipping: shippingCharge,
      total,
      payment_method: "stripe",
      payment_id,
      payment_status: paymentStatus,
      brand: brands,
      cart_items: cartItems,
      shipping_address: shippingAddressObj,
      delivery_date: {
  date: deliveryDate,
  is_rush_delivery: isRushDelivery
}
    };

    const res = await fetch(`${apiUrl}/api/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.success) {
      navigate("/thank-you", { state: { orderId: data.order_id } });
    }
  };

  if (loading) {
    return <p className="text-center p-10">Loading checkout...</p>;
  }

  if (cartItems.length === 0) {
    return (
      <div>
        <Back title="Checkout" />
        <div className="text-center py-20">
          <h2 className="text-3xl font-bold text-gray-700">Your cart is empty</h2>
          <a
            href="/"
            className="mt-6 inline-block bg-black text-white px-6 py-3 rounded-lg"
          >
            Continue Shopping
          </a>
        </div>
      </div>
    );
  }

  // --------------------
  // UI RETURN
  // --------------------
  return (
    <div>
      <Back title="Checkout" />
      <SEO
        title='Checkout'
        description='Personalize Patches For Hats'
        keywords='Personalize Patches For Hats'
      />

      <div className="mx-5 sm:mx-16">
        <h2 className="text-2xl font-semibold mb-6 mt-20 md:text-3xl lg:text-5xl">
          Checkout
        </h2>

        <div className="lg:flex lg:justify-between">
          {/* -------------------
              FORMS SECTION
          ------------------- */}
          <div className="lg:w-10/12 lg:mr-10 space-y-6">
            
            {/* BILLING FORM */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Billing details</h3>
              <form className="space-y-6">
                {/* TWO COLUMN GRID */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* FIRST NAME */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full mt-2 px-4 py-3 rounded-xl border
                        ${errors.firstName ? "border-red-500" : "border-gray-300"}
                        focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Enter your first name"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setErrors({ ...errors, firstName: false });
                      }}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">First name is required</p>
                    )}
                  </div>

                  {/* LAST NAME */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full mt-2 px-4 py-3 rounded-xl border
                        ${errors.lastName ? "border-red-500" : "border-gray-300"}
                        focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Enter your last name"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setErrors({ ...errors, lastName: false });
                      }}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">Last name is required</p>
                    )}
                  </div>

                  {/* ADDRESS (FULL WIDTH) */}
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full mt-2 px-4 py-3 rounded-xl border
                        ${errors.address ? "border-red-500" : "border-gray-300"}
                        focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Enter your address"
                      value={address}
                      onChange={(e) => {
                        setAddress(e.target.value);
                        setErrors({ ...errors, address: false });
                      }}
                    />
                    {errors.address && (
                      <p className="text-red-500 text-sm mt-1">Address is required</p>
                    )}
                  </div>

                  {/* CITY */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Town / City <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full mt-2 px-4 py-3 rounded-xl border
                        ${errors.city ? "border-red-500" : "border-gray-300"}
                        focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Enter your city"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        setErrors({ ...errors, city: false });
                      }}
                    />
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">City is required</p>
                    )}
                  </div>

                  {/* COUNTRY */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full mt-2 px-4 py-3 rounded-xl border
                        ${errors.country ? "border-red-500" : "border-gray-300"}
                        focus:outline-none focus:ring-2 focus:ring-green-500`}
                      value={country}
                      onChange={(e) => {
                        setCountry(e.target.value);
                        setErrors({ ...errors, country: false });
                      }}
                    >
                      <option value="">Select a country</option>
                      {countries.map((c) => (
                        <option key={c.code} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                    {errors.country && (
                      <p className="text-red-500 text-sm mt-1">Country is required</p>
                    )}
                  </div>

                  {/* ZIP */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Postcode / ZIP <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full mt-2 px-4 py-3 rounded-xl border
                        ${errors.zip ? "border-red-500" : "border-gray-300"}
                        focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Enter ZIP"
                      value={zip}
                      onChange={(e) => {
                        setZip(e.target.value);
                        setErrors({ ...errors, zip: false });
                      }}
                    />
                    {errors.zip && (
                      <p className="text-red-500 text-sm mt-1">ZIP is required</p>
                    )}
                  </div>

                  {/* MOBILE */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Mobile <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full mt-2 px-4 py-3 rounded-xl border
                        ${errors.mobile ? "border-red-500" : "border-gray-300"}
                        focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Enter mobile number"
                      value={mobile}
                      onChange={(e) => {
                        setMobile(e.target.value);
                        setErrors({ ...errors, mobile: false });
                      }}
                    />
                    {errors.mobile && (
                      <p className="text-red-500 text-sm mt-1">Mobile number is required</p>
                    )}
                  </div>

                  {/* EMAIL (FULL WIDTH) */}
                  <div className="md:col-span-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      className={`w-full mt-2 px-4 py-3 rounded-xl border
                        ${errors.email ? "border-red-500" : "border-gray-300"}
                        focus:outline-none focus:ring-2 focus:ring-green-500`}
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors({ ...errors, email: false });
                      }}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">Email is required</p>
                    )}
                  </div>

                </div>
              </form>
            </div>

            {/* SHIPPING ADDRESS SECTION */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              {/* Checkbox for same as billing */}
              <div className="mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isShippingSame}
onChange={(e) => {
  const checked = e.target.checked;
  setIsShippingSame(checked);

  if (checked) {
    // ✅ ON → billing copy
    setShippingFirstName(firstName || "");
    setShippingLastName(lastName || "");
    setShippingAddress(address || "");
    setShippingCity(city || "");
    setShippingCountry(country || "");
    setShippingZip(zip || "");
    setShippingErrors({});
  } else {
    // ❌ OFF → EMPTY fields
    setShippingFirstName("");
    setShippingLastName("");
    setShippingAddress("");
    setShippingCity("");
    setShippingCountry("");
    setShippingZip("");
  }
}}  
                    className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="ml-3 text-gray-700 font-medium">
                    Shipping address same as billing address
                  </span>
                </label>
              </div>

              {/* SHIPPING FORM - Show only when checkbox is unchecked */}
              {!isShippingSame && (
                <div>
                  <h3 className="text-xl font-semibold mb-4">Shipping details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* SHIPPING FIRST NAME */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={`w-full mt-2 px-4 py-3 rounded-xl border
                          ${shippingErrors.shippingFirstName ? "border-red-500" : "border-gray-300"}
                          focus:outline-none focus:ring-2 focus:ring-green-500`}
                        placeholder="Enter shipping first name"
                        value={shippingFirstName}
                        onChange={(e) => {
                          setShippingFirstName(e.target.value);
                          setShippingErrors({ ...shippingErrors, shippingFirstName: false });
                        }}
                      />
                      {shippingErrors.shippingFirstName && (
                        <p className="text-red-500 text-sm mt-1">Shipping first name is required</p>
                      )}
                    </div>

                    {/* SHIPPING LAST NAME */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={`w-full mt-2 px-4 py-3 rounded-xl border
                          ${shippingErrors.shippingLastName ? "border-red-500" : "border-gray-300"}
                          focus:outline-none focus:ring-2 focus:ring-green-500`}
                        placeholder="Enter shipping last name"
                        value={shippingLastName}
                        onChange={(e) => {
                          setShippingLastName(e.target.value);
                          setShippingErrors({ ...shippingErrors, shippingLastName: false });
                        }}
                      />
                      {shippingErrors.shippingLastName && (
                        <p className="text-red-500 text-sm mt-1">Shipping last name is required</p>
                      )}
                    </div>

                    {/* SHIPPING ADDRESS (FULL WIDTH) */}
                    <div className="md:col-span-2">
                      <label className="text-sm font-semibold text-gray-700">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={`w-full mt-2 px-4 py-3 rounded-xl border
                          ${shippingErrors.shippingAddress ? "border-red-500" : "border-gray-300"}
                          focus:outline-none focus:ring-2 focus:ring-green-500`}
                        placeholder="Enter shipping address"
                        value={shippingAddress}
                        onChange={(e) => {
                          setShippingAddress(e.target.value);
                          setShippingErrors({ ...shippingErrors, shippingAddress: false });
                        }}
                      />
                      {shippingErrors.shippingAddress && (
                        <p className="text-red-500 text-sm mt-1">Shipping address is required</p>
                      )}
                    </div>

                    {/* SHIPPING CITY */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        Town / City <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={`w-full mt-2 px-4 py-3 rounded-xl border
                          ${shippingErrors.shippingCity ? "border-red-500" : "border-gray-300"}
                          focus:outline-none focus:ring-2 focus:ring-green-500`}
                        placeholder="Enter shipping city"
                        value={shippingCity}
                        onChange={(e) => {
                          setShippingCity(e.target.value);
                          setShippingErrors({ ...shippingErrors, shippingCity: false });
                        }}
                      />
                      {shippingErrors.shippingCity && (
                        <p className="text-red-500 text-sm mt-1">Shipping city is required</p>
                      )}
                    </div>

                    {/* SHIPPING COUNTRY */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        className={`w-full mt-2 px-4 py-3 rounded-xl border
                          ${shippingErrors.shippingCountry ? "border-red-500" : "border-gray-300"}
                          focus:outline-none focus:ring-2 focus:ring-green-500`}
                        value={shippingCountry}
                        onChange={(e) => {
                          setShippingCountry(e.target.value);
                          setShippingErrors({ ...shippingErrors, shippingCountry: false });
                        }}
                      >
                        <option value="">Select a country</option>
                        {countries.map((c) => (
                          <option key={c.code} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                      {shippingErrors.shippingCountry && (
                        <p className="text-red-500 text-sm mt-1">Shipping country is required</p>
                      )}
                    </div>

                    {/* SHIPPING ZIP */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        Postcode / ZIP <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={`w-full mt-2 px-4 py-3 rounded-xl border
                          ${shippingErrors.shippingZip ? "border-red-500" : "border-gray-300"}
                          focus:outline-none focus:ring-2 focus:ring-green-500`}
                        placeholder="Enter shipping ZIP"
                        value={shippingZip}
                        onChange={(e) => {
                          setShippingZip(e.target.value);
                          setShippingErrors({ ...shippingErrors, shippingZip: false });
                        }}
                      />
                      {shippingErrors.shippingZip && (
                        <p className="text-red-500 text-sm mt-1">Shipping ZIP is required</p>
                      )}
                    </div>

                  </div>
                </div>
              )}
            </div>

            {/* NOTES */}
            <div>
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-xl
                  focus:outline-none focus:ring-2 focus:ring-green-500
                  focus:border-green-500 transition-all duration-200 placeholder-gray-400"
                rows="5"
                placeholder="Order notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* -------------------
              ORDER SUMMARY
          ------------------- */}
          <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-10 w-full lg:w-8/12">
            <table className="w-full text-sm text-left text-gray-600">
              <thead className="text-xs uppercase bg-gray-50 font-semibold">
                <tr>
                  <th className="px-6 py-3">Product</th>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Unit Price</th>
                  <th className="px-6 py-3">Qty</th>
                  <th className="px-6 py-3">Total</th>
                </tr>
              </thead>

       <tbody>
  {Array.from(
    new Map(cartItems.map((item) => [item.product_name, item])).values()
  ).map((item) => {
    const totalPrice = Number(item.price);
    const unitPrice = item.qty ? totalPrice / item.qty : 0;

    return (
      <tr key={item.id} className="border-b">
        <td className="px-6 py-4">
          <img
            src={
              item.upload_image
                ? `${apiUrl}/storage/${item.upload_image}`
                : "../img/no-image.png"
            }
            className="w-14 h-14 rounded-full object-cover"
          />
        </td>

        <td className="px-6 py-4">{item.product_name}</td>

        <td className="px-6 py-4">
          ${unitPrice.toFixed(2)}
        </td>

        <td className="px-6 py-4">{item.qty}</td>

        <td className="px-6 py-4">
          ${totalPrice.toFixed(2)}
        </td>
      </tr>
    );
  })}
</tbody>
            </table>

            {/* Delivery Date Section */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isRushDelivery}
                      onChange={(e) => setIsRushDelivery(e.target.checked)}
                      className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="ml-3 text-gray-700 font-medium">
                      Rush Delivery (7 days)
                    </span>
                  </label>
                  {/* <div className="text-sm text-gray-600">
                    Expected Delivery Date: <strong className="text-green-600">{deliveryDate}</strong>
                  </div> */}
                </div>
                {isRushDelivery && (
                  <div className="text-sm text-orange-600 font-semibold">
                    +20% Rush Fee
                  </div>
                )}
              </div>
            </div>

            {/* Subtotal */}
            <div className="flex justify-between px-6 py-4 text-gray-700">
              <h3>Subtotal</h3>
              <span>${subtotal.toFixed(2)}</span>
            </div>

            <hr />

            {/* Shipping */}
               
            <div className="flex justify-between px-6 py-4 text-gray-700">
              <h3>Shipping {isRushDelivery && <span className="text-sm text-orange-600">(Rush Delivery)</span>}</h3>
              <span>${shippingCharge.toFixed(2)}</span>
            </div>
          <div className="flex justify-between px-6 py-4 text-gray-700">
                    Expected Delivery Date: <strong className="text-green-600">{deliveryDate}</strong>
                  </div>

            <hr />

            {/* Total */}
            <div className="flex justify-between px-6 py-6 text-gray-700 text-lg font-bold">
              <h3 className="uppercase">Total</h3>
              <span>${total.toFixed(2)}</span>
            </div>

            <hr />

            {/* PLACE ORDER */}
            <div className="px-4 py-6">
              <StripeCheckout
                orderData={{
                  first_name: firstName,
                  last_name: lastName,
                  email,
                  total,
                }}
                validateForm={validateAllForms}
                onSuccess={async (paymentId) => {
                  await placeOrder(paymentId, "paid");
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;